import { toast } from "sonner";

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB
const MAX_FILES = 10;

const ALLOWED_MIME_TYPES = new Set([
  // Images
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  // Videos
  "video/mp4",
  "video/webm",
  "video/quicktime",
  // Audio
  "audio/mpeg",
  "audio/wav",
  "audio/ogg",
  "audio/webm",
  // Documents
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "text/plain",
  "text/markdown",
  "text/x-markdown",
  "text/csv",
  "application/json",
  "application/zip",
  "application/x-tar",
  "application/gzip",
]);

/**
 * Extract files from clipboard data.
 * Handles: copied files (from Finder), screenshots, and images from web.
 */
export function getFilesFromClipboard(clipboardData: DataTransfer): File[] {
  // Check direct files first (file system copies)
  if (clipboardData.files.length > 0) {
    return Array.from(clipboardData.files);
  }

  // Check items (screenshots, web images)
  const files: File[] = [];
  for (const item of Array.from(clipboardData.items)) {
    if (item.kind === "file") {
      const file = item.getAsFile();
      if (file) files.push(file);
    }
  }
  return files;
}

interface ValidationResult {
  valid: File[];
  errors: string[];
}

/**
 * Validate and filter files, returning valid files and error messages.
 */
export function validatePastedFiles(
  files: File[],
  currentCount: number,
): ValidationResult {
  const valid: File[] = [];
  const errors: string[] = [];
  const remainingSlots = MAX_FILES - currentCount;

  if (remainingSlots <= 0) {
    errors.push("Maximum 10 files allowed");
    return { valid, errors };
  }

  for (const file of files.slice(0, remainingSlots)) {
    if (file.size > MAX_FILE_SIZE) {
      errors.push(`"${file.name}" exceeds 25MB limit`);
    } else if (file.type && !ALLOWED_MIME_TYPES.has(file.type)) {
      // Only reject if type is non-empty AND not in allowlist
      // Files with empty MIME types (e.g., .log, .yaml, .md) pass through
      errors.push(`"${file.name}" type not supported`);
    } else {
      valid.push(file);
    }
  }

  if (files.length > remainingSlots) {
    errors.push(`${files.length - remainingSlots} file(s) skipped (max 10)`);
  }

  return { valid, errors };
}

/**
 * Show toast for validation errors.
 */
export function showPasteErrors(errors: string[]): void {
  if (errors.length === 0) return;
  if (errors.length === 1) {
    toast.error(errors[0]);
  } else {
    toast.error(`${errors.length} files rejected`, {
      description: errors.slice(0, 3).join(", "),
    });
  }
}

// --- Multi-message copy formatting ---

export interface ClipboardMessage {
  displayName: string;
  timestamp: number;
  content: string;
  kind?: string | null;
  payload?: Record<string, unknown> | null;
}

// Map new kind values to the legacy cardType vocabulary used in
// cardToClipboardText. Kept local so callers don't need to know both names.
function kindToCardType(kind: string): string | null {
  switch (kind) {
    case "terminal_output":
      return "terminal-output";
    case "terminal_snapshot":
      return "terminal-snapshot";
    case "command_history":
      return "command-history";
    case "run":
      return "run";
    case "calendar":
      return "calendar";
    case "email":
      return "email";
    default:
      return null;
  }
}

function str(data: Record<string, unknown>, key: string): string {
  const v = data[key];
  return typeof v === "string" ? v : "";
}

function num(data: Record<string, unknown>, key: string): number | undefined {
  const v = data[key];
  return typeof v === "number" ? v : undefined;
}

export function cardToClipboardText(
  cardType: string,
  data: Record<string, unknown>,
): string {
  switch (cardType) {
    case "terminal-output":
      return `Terminal output:\n${str(data, "content")}`;
    case "terminal-snapshot":
      return `Terminal snapshot:\n${str(data, "plain")}`;
    case "command-history": {
      const command = str(data, "command");
      const output = str(data, "outputPreview");
      const exitCode = num(data, "exitCode");
      const exitLine =
        exitCode !== undefined && exitCode !== 0 ? `\n[exit ${exitCode}]` : "";
      return `$ ${command}${output ? `\n${output}` : ""}${exitLine}`;
    }
    case "run": {
      const command = str(data, "command");
      const output = str(data, "output");
      const exitCode = num(data, "exitCode");
      const exitLine =
        exitCode !== undefined && exitCode !== 0 ? `\n[exit ${exitCode}]` : "";
      return `$ ${command}${output ? `\n${output}` : ""}${exitLine}`;
    }
    case "terminal-live":
      return `[Live terminal session]`;
    case "terminal-recording": {
      const duration = num(data, "duration");
      return duration !== undefined
        ? `[Terminal recording, ${duration}s]`
        : `[Terminal recording]`;
    }
    case "calendar": {
      const title = str(data, "title") || "Event";
      const start = str(data, "start");
      return start ? `[Calendar: ${title} — ${start}]` : `[Calendar: ${title}]`;
    }
    case "email": {
      const subject = str(data, "subject") || "(no subject)";
      const from = str(data, "from");
      return from
        ? `[Email: ${subject} — from ${from}]`
        : `[Email: ${subject}]`;
    }
    default: {
      const app = str(data, "app") || str(data, "sourceApp") || "Tool";
      const title =
        str(data, "title") ||
        str(data, "subject") ||
        str(data, "name") ||
        "Shared item";
      const url = str(data, "url") || str(data, "link");
      return url ? `[${app}: ${title}] ${url}` : `[${app}: ${title}]`;
    }
  }
}

export function renderContentForClipboard(
  message: Pick<ClipboardMessage, "content" | "kind" | "payload"> | string,
): string {
  const msg =
    typeof message === "string"
      ? {
          content: message,
          kind: "text" as string | null | undefined,
          payload: null,
        }
      : message;
  const cardType = msg.kind ? kindToCardType(msg.kind) : null;
  if (cardType && msg.payload) {
    return cardToClipboardText(cardType, msg.payload);
  }
  return msg.content;
}

export function formatMessagesForClipboard(
  messages: ClipboardMessage[],
): string {
  return messages
    .map((msg) => {
      const time = new Date(msg.timestamp).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
      return `[${msg.displayName}, ${time}]\n${renderContentForClipboard(msg)}`;
    })
    .join("\n\n");
}

export function getSelectedMessageIds(
  selection: Selection,
  container: HTMLElement,
): string[] | null {
  if (!selection.rangeCount || selection.isCollapsed) return null;

  const range = selection.getRangeAt(0);
  const allMessageEls =
    container.querySelectorAll<HTMLElement>("[data-message-id]");

  const selectedIds: string[] = [];
  for (const el of allMessageEls) {
    if (range.intersectsNode(el)) {
      const id = el.dataset.messageId;
      if (id) selectedIds.push(id);
    }
  }

  return selectedIds.length > 0 ? selectedIds : null;
}

export function selectionIncludesAnyMessageHeader(
  selection: Selection,
  container: HTMLElement,
): boolean {
  if (!selection.rangeCount || selection.isCollapsed) return false;

  const range = selection.getRangeAt(0);
  const headers = container.querySelectorAll<HTMLElement>(
    "[data-message-header]",
  );

  for (const el of headers) {
    if (range.intersectsNode(el)) return true;
  }
  return false;
}
